/**
 * Единственная точка HTTP-запросов приложения.
 *
 * Сессия — httpOnly-cookie, поэтому токенов в коде нет и заголовок Authorization
 * не собирается (спека §8.1). Здесь же единый разбор ошибок: сервер отдаёт
 * доменную ошибку кодом и телом, а UI показывает текст из неё, а не «Произошла ошибка».
 */

import type { LoginBody, LoginResponse, MeResponse, OkResponse, SessionUser } from '@cc/shared';

/** Ошибка запроса с кодом, по которому UI решает, что показать и куда вести */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Сессия кончилась или отозвана — нужен вход заново */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Состояние на сервере изменилось: карточку уже закрыли, раздача уже идёт */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

export type QueryValue = string | number | boolean | undefined;

export interface RequestOptions {
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
}

/** Базовый путь. В деве Vite проксирует /api на cc-api, в бою их сводит nginx */
const BASE_PATH = '/api';

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

/** Вызывается один раз при старте приложения: 401 → возврат на страницу входа */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = `${BASE_PATH}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs === '' ? url : `${url}?${qs}`;
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const init: RequestInit = {
    method,
    // Cookie сессии ходит сама; на своём домене этого достаточно
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  };

  return readResponse<T>(await fetch(buildUrl(path, options.query), init));
}

/**
 * Загрузка файла базы. Тело едет сырым буфером, а параметры разбора — в строке
 * запроса: сервер читает файл как есть, кодировку и разделитель выбирает человек
 * (автоопределение врёт на кириллице и превращает базу в «Ð˜Ð²Ð°Ð½Ð¾Ð²»).
 * Персданных в строке запроса нет — там только цена, разделитель и имя файла.
 */
async function postFile<T>(path: string, file: Blob, options: RequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: 'POST',
    credentials: 'same-origin',
    // Тип задаём явно: у файла из диалога он бывает пустым, и тогда Fastify
    // не найдёт разборщик тела и ответит 415
    headers: { 'Content-Type': 'text/csv' },
    body: file,
    ...(options.signal ? { signal: options.signal } : {}),
  };

  return readResponse<T>(await fetch(buildUrl(path, options.query), init));
}

/**
 * Загрузка звукового файла (press-one): тело едет сырым WAV, имя — в строке
 * запроса. Отдельно от postFile, потому что тип содержимого другой и сервер
 * выбирает разборщик по нему.
 */
async function postAudio<T>(path: string, file: Blob, name: string): Promise<T> {
  return readResponse<T>(
    await fetch(buildUrl(path, { name }), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'audio/wav' },
      body: file,
    }),
  );
}

/** Разбор ответа один на все методы: иначе 401 или доменная ошибка обработается по-разному */
async function readResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    onUnauthorized?.();
    throw new ApiError(401, 'unauthorized', 'Сессия истекла, войдите заново');
  }

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const shape = payload as { code?: string; message?: string; details?: unknown } | null;
    throw new ApiError(
      response.status,
      shape?.code ?? 'unknown',
      shape?.message ?? `Запрос не выполнен (${String(response.status)})`,
      shape?.details,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('POST', path, body, options),
  // Загрузка базы файлом: тело сырое, параметры разбора — в query
  postFile: <T>(path: string, file: Blob, options?: RequestOptions): Promise<T> =>
    postFile<T>(path, file, options),
  // Звук для press-one: сырой WAV, имя в query
  postAudio: <T>(path: string, file: Blob, name: string): Promise<T> =>
    postAudio<T>(path, file, name),
  // PUT — замена целиком (настройка, недельный график), PATCH — частичная правка
  put: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PATCH', path, body, options),
  delete: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>('DELETE', path, undefined, options),
};

/**
 * Кто вошёл. 401 здесь — нормальный ответ «никто», а не сбой.
 * Оба маршрута сессии заворачивают пользователя в поле user (§8.1) — разворачиваем
 * здесь, чтобы дальше по приложению ездил ровно тот тип, что описан контрактом.
 */
export async function fetchSession(): Promise<SessionUser> {
  const response = await api.get<MeResponse>('/auth/me');
  return response.user;
}

export async function login(input: LoginBody): Promise<SessionUser> {
  const response = await api.post<LoginResponse>('/auth/login', input);
  return response.user;
}

export async function logout(): Promise<void> {
  // Сервер отвечает {ok:true}, а не пустым телом — тип должен это отражать
  await api.post<OkResponse>('/auth/logout');
}
